import * as React from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Filter, X } from "lucide-react";
import { cn } from "@/lib/utils";

const MOBILE_BREAKPOINT = 768;

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const media = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const update = () => setIsMobile(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return isMobile;
}

export function FilterPanel({
  activeCount = 0,
  onClear,
  open,
  onOpenChange,
  title = "Filters",
  mobileTitle = "Filter options",
  children,
  onApply,
  className,
  drawerClassName,
}) {
  const isMobile = useIsMobile();
  const trigger = (
    <Button
      type="button"
      variant={activeCount > 0 ? "secondary" : "outline"}
      size="sm"
      className="h-9 gap-2"
      aria-label={activeCount > 0 ? `Filters, ${activeCount} active` : "Open filters"}
    >
      <Filter className="h-4 w-4" aria-hidden="true" />
      Filters
      {activeCount > 0 && (
        <Badge variant="default" className="h-5 min-w-5 justify-center rounded-full px-1.5 text-[11px]">
          {activeCount}
        </Badge>
      )}
    </Button>
  );

  const body = (
    <div className={cn("flex min-h-0 flex-col", className)}>
      <div className="flex items-start justify-between gap-4 border-b border-border pb-4">
        <div>
          <h2 className="font-display text-lg font-semibold leading-tight">{title}</h2>
          <p className="mt-1 text-xs text-muted-foreground">Narrow the list without losing your place.</p>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={onClear} className="-mr-2 -mt-1 shrink-0 text-xs">
          Clear all
        </Button>
      </div>
      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto py-5 pr-1">{children}</div>
      <div className="flex gap-2 border-t border-border pt-4">
        <Button type="button" variant="outline" onClick={onClear} className="flex-1">
          <X className="mr-2 h-4 w-4" aria-hidden="true" />
          Clear
        </Button>
        <Button type="button" onClick={onApply} className="flex-1">
          <Check className="mr-2 h-4 w-4" aria-hidden="true" />
          Apply filters
        </Button>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange} shouldScaleBackground>
        <DrawerTrigger asChild>{trigger}</DrawerTrigger>
        <DrawerContent className={cn("max-h-[88vh]", drawerClassName)}>
          <DrawerHeader className="px-5 pb-2 text-left">
            <DrawerTitle>{mobileTitle}</DrawerTitle>
          </DrawerHeader>
          <div className="min-h-0 overflow-hidden px-5 pb-5">{body}</div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange} modal>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="start" sideOffset={8} className="max-h-[80vh] w-[min(92vw,420px)] overflow-hidden p-5">
        {body}
      </PopoverContent>
    </Popover>
  );
}

export function FilterSection({ title, children, className }) {
  return (
    <section className={cn("space-y-3", className)}>
      <h3 className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">{title}</h3>
      <div className="grid gap-3">{children}</div>
    </section>
  );
}
